import React from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * PaymentMilestonesPanel — extracted from RABilling.jsx as part of the
 * iter46 code-review refactor. Renders the Payment Milestones editor:
 *
 *   • Σ = <supply + install + tnc> validator chip (green when 100%)
 *   • 4 preset buttons (85/10/5, 70/20/10, 80/15/5, 60/30/10)
 *   • "Apply to all N item(s)" bulk-update button
 *   • Contextual copy on when to use each preset
 *
 * Props:
 *   • boq        — current BOQ document (must have default_supply_pct/install_pct/tnc_pct + items[])
 *   • setBoq(fn) — state setter for the parent's boq state
 */
const PRESETS = [
  { label: "85 / 10 / 5", s: 85, i: 10, t: 5 },
  { label: "70 / 20 / 10", s: 70, i: 20, t: 10 },
  { label: "80 / 15 / 5", s: 80, i: 15, t: 5 },
  { label: "60 / 30 / 10", s: 60, i: 30, t: 10 },
];

export default function PaymentMilestonesPanel({ boq, setBoq }) {
  const sp = Number(boq.default_supply_pct ?? 85);
  const ip = Number(boq.default_install_pct ?? 10);
  const tp = Number(boq.default_tnc_pct ?? 5);
  const sum = sp + ip + tp;
  const ok = Math.abs(sum - 100) < 0.01;
  const itemCount = (boq.items || []).length;

  const applyToAll = () => {
    if (!window.confirm(`Apply default milestone splits (${sp}% Supply · ${ip}% Install · ${tp}% T&C) to ALL ${itemCount} item(s)? Any per-item overrides will be replaced.`)) return;
    setBoq((b) => ({ ...b, items: (b.items || []).map((it) => ({ ...it, supply_pct: sp, install_pct: ip, tnc_pct: tp })) }));
    toast.success(`Applied ${sp}/${ip}/${tp}% to all ${itemCount} items`);
  };

  return (
    <div className="mt-3 border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="payment-milestones-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2">
          Payment Milestones
          <span className={`text-[10px] font-mono normal-case px-1.5 py-0.5 rounded-sm ${ok ? "bg-emerald-100 text-emerald-800 border border-emerald-200" : "bg-red-100 text-red-800 border border-red-200"}`}>
            Σ = {sum.toFixed(1)}% {ok ? "✓" : "must be 100"}
          </span>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              className="h-7 px-2.5 text-[11px] font-mono rounded-sm border border-slate-300 bg-white hover:bg-slate-100 text-slate-700"
              onClick={() => setBoq((b) => ({ ...b, default_supply_pct: p.s, default_install_pct: p.i, default_tnc_pct: p.t }))}
              data-testid={`preset-${p.label.replace(/[^0-9]/g, "-")}`}
            >{p.label}</button>
          ))}
          <Button
            size="sm"
            variant="outline"
            disabled={!ok || itemCount === 0}
            className="h-7 rounded-sm text-[11px] border-[#EA580C] text-[#EA580C] hover:bg-orange-50"
            onClick={applyToAll}
            data-testid="apply-milestones-to-all"
          >Apply to all {itemCount} item(s)</Button>
        </div>
      </div>
      <div className="text-[10.5px] text-slate-600 leading-relaxed">
        Milestone splits define what % of each item's value can be billed at each stage. These defaults appear on new items and on the exported ABSTRACT-format Excel. Individual items can still override these values in the Items tab. Common presets: <span className="font-mono">85/10/5</span> for VRF equipment · <span className="font-mono">70/20/10</span> for civil-heavy scope · <span className="font-mono">60/30/10</span> for install-intensive.
      </div>
    </div>
  );
}
